import { extractJson } from '../utils/jsonParser.js';

const VALID_DIFFICULTIES = ['easy', 'medium', 'hard'];

// 多样性种子：每次随机抽取组合注入提示词，避免 LLM 反复生成雷同的汤
const SOUP_TYPES = [
  '连锁巧合（一个普通动作触发一连串巧合，最终造成意外结果）',
  '伪装成意外的谋杀',
  '伪装成谋杀的意外',
  '伪装成自杀的意外，或伪装成意外的自杀',
  '黑暗讽刺（社会习俗、尴尬真相、禁忌主题造成的悲剧）',
  '不可能场景（现场看似无解，真相却简单但出人意料）',
  '非死亡类（某人做了奇怪的事、某物出现在不该出现的地方，需要横向思维还原）',
];

const SETTINGS = [
  '医院', '海上渔船', '雪山小屋', '写字楼办公室', '婚礼现场', '马戏团',
  '火车站', '沙漠公路', '深夜便利店', '学校', '剧院后台', '电梯里',
  '露营地', '博物馆', '机场', '老式公寓楼', '游乐园', '法庭',
  '直播间', '温泉旅馆', '停车场', '图书馆', '裁缝铺', '灯塔',
];

const TWIST_MECHANISMS = [
  '身份误认（某人被错当成另一个人）',
  '时间错位（事件发生的顺序或时间与表面看起来不同）',
  '视角欺骗（观察或叙述角度造成了错误印象）',
  '双关或误解（一句话、一个词被理解错了）',
  '物品的非常规用途',
  '当事人自己的行为引发的连锁反应',
  '不在场的人才是关键',
  '动物或自然力量扮演了关键角色',
  '职业习惯或特殊技能导致的意外',
  '信息差造成的悲剧（某人不知道某个关键事实）',
];

const SKELETON_SYSTEM_PROMPT = `你是一名海龟汤（情境推理谜题）主持人。你的任务是设计海龟汤的“核心骨架”：核心反转与核心要素清单。只返回合法 JSON。

核心反转（twist）是本题的灵魂：真相不是直白的谋杀，而是需要“转个弯”才能想到的巧妙解释。例如：猎人开枪引发雪崩压死自己；搬钢琴时楼上的人松手砸死楼下的人。

设计要求：
1. 必须围绕给定的“题目类型 / 场景 / 反转机制”三个种子自由发挥，设计出独特的情境。不要套用经典名题，也不要复用上面两个示例。
2. 核心反转要具体、可执行、能用一两句话讲清楚“真正发生了什么”，并且能解释汤面会呈现出的异常现象。
3. 鼓励黑暗、荒诞、讽刺的基调，但逻辑必须自洽。
4. 难度：easy（线索明显，反转简单）、medium（需要联想）、hard（误导多或需要跳出常规思维）。
5. 所有文本使用简体中文（zh-CN）。
6. 只返回合法 JSON，不要 markdown 代码块，不要额外说明。

JSON schema：
{
  "id": "string (英文小写短标识，如 snowy-hunting-accident)",
  "title": "string (海龟汤标题，如 雪山猎人之死)",
  "difficulty": "easy|medium|hard",
  "twist": "string (核心反转：一两句话说明真正发生了什么)",
  "facts": {
    "culprit": "string (真正的责任人/凶手；若没有责任人，留空字符串)",
    "victim": "string (受害者/死者；若没有受害者，留空字符串)",
    "murderWeapon": "string (关键手段/“凶器”，可以是概念、物品、自然现象等；若不适用，留空字符串)",
    "motive": "string (动机；若没有动机，留空字符串)",
    "keyClues": ["string (指向汤底的关键线索)"],
    "timeline": ["string (关键事件时间线，可留空数组)"]
  }
}`;

const NARRATIVE_SYSTEM_PROMPT = `你是一名海龟汤（情境推理谜题）主持人。核心反转与要素清单已经定好，你的任务是为它撰写“汤面”与“汤底”。只返回合法 JSON。

汤面（description）要求：
1. 2-4 段，短而有力，只呈现表面的客观现象与异常之处，让玩家觉得“奇怪/不对劲”。
2. 绝不透露汤底、关键动作或隐藏真相。可以包含误导元素，让玩家先往谋杀、自杀、超自然等方向猜测。
3. 不希望玩家知道的信息，汤面中就不要写出；汤面中的客观事实必须与要素清单一致，不能互相矛盾。
4. 不要写成侦探小说的长篇现场勘查，不要出现“真相是”“原来”等剧透句式。

汤底（fullStory）要求：
1. 解释真实经过、核心反转、汤面中哪些元素是误导、为什么玩家会猜错。
2. 必须与给定的核心反转（twist）和要素清单（facts）完全一致，不得另起炉灶。

所有文本使用简体中文（zh-CN）。只返回合法 JSON，不要 markdown 代码块，不要额外说明。

JSON schema：
{
  "description": "string (汤面：2-4 段表面情境，只呈现异常，不剧透)",
  "fullStory": "string (汤底：解释真实经过、反转与误导)"
}`;

function pickRandom(list) {
  return list[Math.floor(Math.random() * list.length)];
}

function buildSkeletonUserPrompt(difficulty, excludeList = []) {
  const lines = [];
  lines.push(`难度：${difficulty}`);
  lines.push(`题目类型：${pickRandom(SOUP_TYPES)}`);
  lines.push(`场景：${pickRandom(SETTINGS)}`);
  lines.push(`反转机制：${pickRandom(TWIST_MECHANISMS)}`);
  if (excludeList.length > 0) {
    lines.push(`请避免与以下已用海龟汤重复：${excludeList.join('、')}`);
  }
  lines.push('请围绕以上种子生成海龟汤核心骨架 JSON。');
  return lines.join('\n');
}

function buildNarrativeUserPrompt(skeleton) {
  return [
    '海龟汤核心骨架：',
    JSON.stringify(skeleton, null, 2),
    '',
    '请根据该骨架撰写汤面与汤底 JSON。记住：description 只能呈现表面情境，不能剧透汤底。',
  ].join('\n');
}

function validateSkeleton(obj) {
  if (!obj || typeof obj !== 'object') {
    throw new Error('Invalid skeleton object');
  }

  const required = ['id', 'title', 'difficulty', 'twist', 'facts'];
  for (const key of required) {
    if (!(key in obj)) {
      throw new Error(`Missing field in skeleton JSON: ${key}`);
    }
  }

  if (!obj.twist || typeof obj.twist !== 'string') {
    throw new Error('Missing or invalid "twist" field');
  }

  if (!VALID_DIFFICULTIES.includes(obj.difficulty)) {
    throw new Error(`Invalid difficulty: ${obj.difficulty}`);
  }

  const facts = obj.facts;
  if (!facts || typeof facts !== 'object') {
    throw new Error('Invalid facts in skeleton JSON');
  }
  const factKeys = ['culprit', 'victim', 'murderWeapon', 'motive', 'keyClues', 'timeline'];
  for (const key of factKeys) {
    if (!(key in facts)) {
      throw new Error(`Missing fact field: ${key}`);
    }
  }

  if (!Array.isArray(facts.keyClues) || !Array.isArray(facts.timeline)) {
    throw new Error('keyClues and timeline must be arrays');
  }

  return obj;
}

function validateNarrative(obj) {
  if (!obj || typeof obj !== 'object') {
    throw new Error('Invalid narrative object');
  }
  for (const key of ['description', 'fullStory']) {
    if (!obj[key] || typeof obj[key] !== 'string') {
      throw new Error(`Missing or invalid "${key}" field`);
    }
  }
  return obj;
}

function validateCase(obj) {
  if (!obj || typeof obj !== 'object') {
    throw new Error('Invalid case object');
  }

  const required = ['id', 'title', 'difficulty', 'description', 'facts', 'fullStory'];
  for (const key of required) {
    if (!(key in obj)) {
      throw new Error(`Missing field in case JSON: ${key}`);
    }
  }

  if (!VALID_DIFFICULTIES.includes(obj.difficulty)) {
    throw new Error(`Invalid difficulty: ${obj.difficulty}`);
  }

  // 确保 id 不为空，否则用 title 生成一个简单标识
  if (!obj.id || typeof obj.id !== 'string') {
    obj.id = String(obj.title)
      .toLowerCase()
      .replace(/[^a-z0-9一-龥]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 32) || `case-${Date.now()}`;
  }

  return obj;
}

// 第一阶段：围绕随机种子生成核心反转与要素清单
async function generateSkeleton(client, { difficulty, excludeList }) {
  const messages = [
    { role: 'system', content: SKELETON_SYSTEM_PROMPT },
    { role: 'user', content: buildSkeletonUserPrompt(difficulty, excludeList) },
  ];

  const response = await client.send(messages, {
    responseFormat: { type: 'json_object' },
  });

  return validateSkeleton(extractJson(response));
}

// 检测汤面是否泄露了 facts 中的敏感信息（凶手/凶器/动机原文措辞）
function findLeaks(description, facts) {
  const sensitive = [facts.culprit, facts.murderWeapon, facts.motive]
    .filter((s) => typeof s === 'string' && s.trim().length >= 2)
    .map((s) => s.trim());
  return sensitive.filter((s) => description.includes(s));
}

// 第二阶段：基于已定的反转撰写汤面与汤底；若汤面剧透，带反馈重试一次
async function generateNarrative(client, skeleton) {
  let userPrompt = buildNarrativeUserPrompt(skeleton);
  let narrative;

  for (let attempt = 0; attempt < 2; attempt += 1) {
    const messages = [
      { role: 'system', content: NARRATIVE_SYSTEM_PROMPT },
      { role: 'user', content: userPrompt },
    ];

    const response = await client.send(messages, {
      responseFormat: { type: 'json_object' },
    });

    narrative = validateNarrative(extractJson(response));

    const leaks = findLeaks(narrative.description, skeleton.facts);
    if (leaks.length === 0) {
      break;
    }
    userPrompt = `${buildNarrativeUserPrompt(skeleton)}\n\n注意：上一版汤面泄露了以下敏感信息：${leaks.join('、')}。请重写汤面，删除或改写相关表述。`;
  }

  return narrative;
}

export async function generateCase(client, { difficulty, excludeList = [] }) {
  const skeleton = await generateSkeleton(client, { difficulty, excludeList });
  const narrative = await generateNarrative(client, skeleton);
  return validateCase({ ...skeleton, ...narrative });
}
